import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  useAddApiKeyToHashKey,
  useRemoveApiKeyFromHashKey,
  useRenameHashKey,
} from "@/features/auth/api/hash.queries";
import { useGetApiToken } from "@/features/tokens/api/token.queries";
import type { IApiKey } from "@/features/tokens/types/token.types";
import type { HashKeyApiResponse } from "@/features/auth/types/hashKeys.types";
import { extractErrorMessage } from "@/lib/errors";
import {
  getMonitoringTypeFromToken,
  MONITORING_BADGE_STYLES,
  createMonitoringIcon,
  type MonitoringType,
} from "@/constants/monitoring";

import z from "zod";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";

interface EditHashKeyFormProps {
  hashKeyData: HashKeyApiResponse;
  onSuccess?: () => void;
}

const editHashKeySchema = z.object({
  name: z.string().min(1, "Name is required"),
});

export default function EditHashKeyForm({
  hashKeyData,
  onSuccess,
}: EditHashKeyFormProps) {
  const form = useForm<z.infer<typeof editHashKeySchema>>({
    resolver: zodResolver(editHashKeySchema),
    defaultValues: {
      name: hashKeyData.hash_key_name,
    },
  });

  const watchedName = form.watch("name");
  const hasNameChanged =
    watchedName.trim() !== hashKeyData?.hash_key_name?.trim();

  const { data: apiKeysData, isLoading: isLoadingApiKeys } = useGetApiToken(
    1,
    100,
    true,
  );
  const apiKeys = (apiKeysData?.api_keys || []).filter(
    (key) => key.active === true,
  );

  const [selectedApiKeys, setSelectedApiKeys] = useState<string[]>([]);

  const [keysToRemove, setKeysToRemove] = useState<string[]>([]);

  const currentApiKeyIds = hashKeyData.api_keys.map((key) => key.api_key_id);

  const { mutate: addApiKeyToHashKey, isPending: isAddingKey } =
    useAddApiKeyToHashKey();
  const { mutate: removeApiKeyFromHashKey, isPending: isRemovingKey } =
    useRemoveApiKeyFromHashKey(false);

  const { mutate: renameHashKey, isPending: isRenamingKey } =
    useRenameHashKey();

  const isPending = isAddingKey || isRemovingKey || isRenamingKey;

  const handleApiKeyToggle = (apiKeyId: string) => {
    const isAlreadyDistributed = currentApiKeyIds.includes(apiKeyId);

    if (isAlreadyDistributed) {
      setKeysToRemove((prev) => {
        if (prev.includes(apiKeyId)) {
          return prev.filter((id) => id !== apiKeyId);
        }
        return [...prev, apiKeyId];
      });
    } else {
      setSelectedApiKeys((prev) => {
        if (prev.includes(apiKeyId)) {
          return prev.filter((id) => id !== apiKeyId);
        }
        return [...prev, apiKeyId];
      });
    }
  };

  const getMonitoringTypeFromApiKey = (
    apiKeyPreview: string,
  ): MonitoringType => {
    return getMonitoringTypeFromToken(apiKeyPreview);
  };

  function onSubmit() {
    const hasChanges =
      selectedApiKeys.length > 0 || keysToRemove.length > 0 || hasNameChanged;

    if (!hasChanges) {
      toast.error("No changes to apply");
      return;
    }

    let addSuccessCount = 0;
    let removeSuccessCount = 0;
    let renameSuccessCount = 0;

    const totalOperations =
      selectedApiKeys.length + keysToRemove.length + (hasNameChanged ? 1 : 0);
    let completedOperations = 0;

    const checkCompletion = () => {
      completedOperations++;
      if (completedOperations === totalOperations) {
        setSelectedApiKeys([]);
        setKeysToRemove([]);

        const messages = [];
        if (addSuccessCount > 0) {
          messages.push(
            `Added ${addSuccessCount} key${addSuccessCount !== 1 ? "s" : ""}`,
          );
        }
        if (removeSuccessCount > 0) {
          messages.push(
            `Removed ${removeSuccessCount} key${removeSuccessCount !== 1 ? "s" : ""}`,
          );
        }
        if (renameSuccessCount > 0) {
          messages.push("Renamed hash key");
        }

        if (messages.length > 0) {
          toast.success(messages.join(" • "));
        }
        onSuccess?.();
      }
    };

    selectedApiKeys.forEach((apiKeyId) => {
      addApiKeyToHashKey(
        { hash_key_id: hashKeyData.hash_key_id, api_key_id: apiKeyId },
        {
          onSuccess: () => {
            addSuccessCount++;
            checkCompletion();
          },
          onError: (err: any) => {
            const message = extractErrorMessage(err);
            toast.error(`Failed to add API key`, {
              description: message,
            });
            checkCompletion();
          },
        },
      );
    });

    keysToRemove.forEach((apiKeyId) => {
      removeApiKeyFromHashKey(
        { hash_key_id: hashKeyData.hash_key_id, api_key_id: apiKeyId },
        {
          onSuccess: () => {
            removeSuccessCount++;
            checkCompletion();
          },
          onError: (err: any) => {
            const message = extractErrorMessage(err);
            toast.error(`Failed to remove API key`, {
              description: message,
            });
            checkCompletion();
          },
        },
      );
    });

    if (hasNameChanged) {
      renameHashKey(
        {
          hash_key_id: hashKeyData.hash_key_id,
          name: form.getValues("name"),
          description: "",
        },
        {
          onSuccess: () => {
            renameSuccessCount++;
            checkCompletion();
          },
          onError: (err: any) => {
            const message = extractErrorMessage(err);
            toast.error(`Failed to rename hash key`, {
              description: message,
            });
            checkCompletion();
          },
        },
      );
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col space-y-4"
      >
        {/* Name Field */}
        <div className="grid grid-cols-1 items-start gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-sm font-bold text-neutral-800">
                  Name <span className="text-red-600">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    placeholder="Hash Key Name"
                    variant="filled"
                    className="h-[45px]"
                  />
                </FormControl>
                <FormMessage className="text-xs text-red-600" />
              </FormItem>
            )}
          />
        </div>
        {/* API Keys Selection */}
        <div className="space-y-2">
          <FormLabel className="text-sm font-bold text-neutral-800">
            API Key Distribution
          </FormLabel>

          {isLoadingApiKeys ? (
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-3 rounded-lg border-2 border-neutral-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex w-full items-center gap-2">
                    <div className="h-[28px] w-[28px] flex-shrink-0 animate-pulse rounded-[8px] bg-neutral-300" />
                    <div className="h-4 w-full min-w-0 flex-1 animate-pulse rounded bg-neutral-300" />
                    <div className="h-6 w-11 flex-shrink-0 animate-pulse rounded-full bg-neutral-300" />
                  </div>
                </div>
              ))}
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="rounded-lg p-4 text-center text-sm text-neutral-500">
              No API keys available
            </div>
          ) : (
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {apiKeys
                .filter((apiKey) => apiKey.active === true)
                .map((apiKey: IApiKey) => {
                  const isNewlySelected = selectedApiKeys.includes(
                    apiKey.api_key_id,
                  );
                  const isAlreadyDistributed = currentApiKeyIds.includes(
                    apiKey.api_key_id,
                  );
                  const isMarkedForRemoval = keysToRemove.includes(
                    apiKey.api_key_id,
                  );

                  const monitoringType = getMonitoringTypeFromApiKey(
                    apiKey.api_key_preview,
                  );
                  const isPremium =
                    apiKey.api_key_preview
                      ?.toLowerCase()
                      ?.includes("premium") || false;

                  const colorClass = isPremium
                    ? "bg-yellow-500"
                    : MONITORING_BADGE_STYLES[monitoringType] || "bg-gray-500";

                  const icon = isPremium ? (
                    <i className="ri-star-line text-md text-white" />
                  ) : (
                    createMonitoringIcon(monitoringType, "text-md", true)
                  );

                  // Determine checked state: distributed (not marked for removal) OR newly selected
                  const isChecked =
                    (isAlreadyDistributed && !isMarkedForRemoval) ||
                    isNewlySelected;

                  // Simple border styling - highlight only when changes are pending
                  const borderClass =
                    isNewlySelected || isMarkedForRemoval
                      ? "border-orange-500 bg-orange-50"
                      : "border-neutral-200 bg-white";

                  return (
                    <div
                      key={apiKey.api_key_id}
                      className={`flex flex-col gap-3 rounded-lg border-2 p-4 transition-all ${borderClass}`}
                    >
                      {/* Token header with icon and switch */}
                      <div className="flex w-full items-center gap-2">
                        <div
                          className={`flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-[8px] ${colorClass} p-2 font-medium text-white transition-transform duration-200 hover:scale-105`}
                        >
                          <div className="flex items-center justify-center text-[20px]">
                            {icon}
                          </div>
                        </div>
                        <div className="w-full min-w-0 flex-1">
                          <div className="line-clamp-2 text-xs leading-4 font-semibold -tracking-wide break-words text-neutral-950">
                            {apiKey.api_key_preview}
                          </div>
                        </div>

                        <Switch
                          checked={isChecked}
                          disabled={isPending}
                          onCheckedChange={() =>
                            handleApiKeyToggle(apiKey.api_key_id)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Submit */}
        <Button
          type="submit"
          className="mt-3 h-[48px] w-full"
          disabled={
            isPending ||
            (selectedApiKeys.length === 0 &&
              keysToRemove.length === 0 &&
              !hasNameChanged)
          }
        >
          {isPending ? (
            <div className="flex h-full items-center justify-center text-white">
              <Spinner size="sm" className="stroke-white!" />
            </div>
          ) : (
            "Update"
          )}
        </Button>
      </form>
    </Form>
  );
}
